import React, { useMemo, useState } from 'react';
import { Alert, Descriptions, Spin, Table, Tag, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
  useGetVariantRecipeConfigQuery,
  useSimulateRecipeCostQuery,
} from '../../services/recipesApi';
import type { SimulatedCostLine } from '../../types/inventory';

const { Text } = Typography;

const formatCurrency = (amount: number) =>
  new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(amount);

interface Props {
  productId: string;
  isMobile: boolean;
}

/**
 * Simulador de costo: eliges modificadores como si armaras el platillo y ves
 * en vivo el costo, margen y consumo de insumos. Usa el mismo cálculo que el
 * descuento de inventario (reglas incluidas), así que lo que se muestra es
 * exactamente lo que una venta real descontaría.
 */
const CostSimulator: React.FC<Props> = ({ productId, isMobile }) => {
  const [selected, setSelected] = useState<Set<string>>(new Set());

  const { data: config, isLoading: loadingConfig } = useGetVariantRecipeConfigQuery(productId);

  // Orden estable para que la caché de RTK Query no distinga A,B de B,A
  const variantIds = useMemo(() => Array.from(selected).sort(), [selected]);
  const { data: simulation, isFetching: simulating } = useSimulateRecipeCostQuery({
    productId,
    variantIds,
  });

  const groupsWithVariants = (config?.groups || []).filter((g) => g.variants.length > 0);

  if (loadingConfig) {
    return (
      <div style={{ textAlign: 'center', padding: 20 }}>
        <Spin />
      </div>
    );
  }

  if (groupsWithVariants.length === 0) {
    return null; // sin modificadores no hay nada que simular
  }

  const toggle = (groupId: string, selectionMode: string, variantId: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(variantId)) {
        next.delete(variantId);
        return next;
      }
      if (selectionMode === 'SingleSelect') {
        // dentro de un grupo de selección única, elegir uno quita el anterior
        const group = groupsWithVariants.find((g) => g.groupId === groupId);
        group?.variants.forEach((v) => next.delete(v.variantId));
      }
      next.add(variantId);
      return next;
    });
  };

  const lineColumns: ColumnsType<SimulatedCostLine> = [
    { title: 'Insumo', dataIndex: 'itemName', key: 'itemName', ellipsis: true },
    {
      title: 'Consumo',
      key: 'quantity',
      align: 'right',
      width: 110,
      render: (_, r) => `${r.quantity} ${r.unit}`,
    },
    {
      title: 'Costo',
      dataIndex: 'totalCost',
      key: 'totalCost',
      align: 'right',
      width: 100,
      render: (v: number) => formatCurrency(v),
    },
  ];

  return (
    <div style={{ marginTop: 16 }}>
      <Text strong style={{ display: 'block', marginBottom: 4 }}>
        Simulador de costo
      </Text>
      <Text type="secondary" style={{ display: 'block', marginBottom: 12, fontSize: 12 }}>
        Elige modificadores para ver el costo real de esa combinación — con las reglas de
        cantidad aplicadas, igual que al descontar inventario en una venta.
      </Text>

      {groupsWithVariants.map((group) => (
        <div key={group.groupId} style={{ marginBottom: 8 }}>
          <Text type="secondary" style={{ fontSize: 12, marginRight: 8 }}>
            {group.groupName}:
          </Text>
          {group.variants.map((v) => (
            <Tag.CheckableTag
              key={v.variantId}
              checked={selected.has(v.variantId)}
              onChange={() => toggle(group.groupId, group.selectionMode, v.variantId)}
            >
              {v.variantName}
            </Tag.CheckableTag>
          ))}
        </div>
      ))}

      <Spin spinning={simulating}>
        {simulation && (
          <>
            <Descriptions column={isMobile ? 2 : 4} bordered size="small" style={{ margin: '12px 0' }}>
              <Descriptions.Item label="Precio">{formatCurrency(simulation.price)}</Descriptions.Item>
              <Descriptions.Item label="Costo">{formatCurrency(simulation.totalCost)}</Descriptions.Item>
              <Descriptions.Item label="Margen">
                <Text
                  strong
                  style={{
                    color:
                      simulation.marginPercentage > 30
                        ? 'var(--color-success-text)'
                        : simulation.marginPercentage >= 15
                          ? 'var(--color-warning-text)'
                          : 'var(--color-error-text)',
                  }}
                >
                  {simulation.marginPercentage.toFixed(1)}%
                </Text>
              </Descriptions.Item>
              <Descriptions.Item label="Food cost">
                {simulation.foodCostPercentage.toFixed(1)}%
              </Descriptions.Item>
            </Descriptions>

            {simulation.warnings.length > 0 && (
              <Alert
                type="warning"
                showIcon
                message={simulation.warnings.join(' ')}
                style={{ marginBottom: 8 }}
              />
            )}

            <Table
              rowKey="inventoryItemId"
              columns={lineColumns}
              dataSource={simulation.lines}
              size="small"
              pagination={false}
            />
          </>
        )}
      </Spin>
    </div>
  );
};

export default CostSimulator;
